// Build-in Libraries

import { InformationCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import type { IGetProjectTeamMembersServiceResData } from '@/api/Sherpa/Project/modules/ProjectTeam/types';
import Button from '@/components/UI/Button';
import { FilterBadge } from '@/components/UI/FilterBadge';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
// Custom Components
import LoadingTable from '@/components/UI/loading/LoadingTable';
import Modal from '@/components/UI/Modal';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '@/components/UI/Table';
// Services
import { useDraws } from '@/hooks/ReactQueryHooks/useProjectDrawQuery';
// Assets
import { formatDate2, formatDate3 } from '@/utils';
import { validationSchemaForFilter } from './validationSchema';

const defaultParams = {
  perPage: 10,
  page: 1,
};

type FilterParamsType = {
  perPage: number;
  page: number;
  openDate?: string;
  closeDate?: string;
  invoiceApproverId?: { label: string; value: number };
};

interface DrawListProps {
  teams?: IGetProjectTeamMembersServiceResData;
}

export const DrawList = ({ teams }: DrawListProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

  const [filterParams, setFilterParams] = useState<FilterParamsType>(defaultParams);

  const fetchDraws = useDraws({
    projectId: Number(projectId),
    params: {
      ...filterParams,
      invoiceApproverId: filterParams?.invoiceApproverId?.value,
    },
  });
  const draws = fetchDraws?.data?.data;
  const projectDrawsCount = fetchDraws?.data?.meta?.count;
  const pageCount = Math.ceil(projectDrawsCount / filterParams.perPage);

  const { openDate, closeDate, invoiceApproverId } = filterParams;

  const submitFilter = (values: Required<FilterParamsType>) => {
    setFilterParams({
      ...defaultParams,
      ...values,
      openDate: values.openDate ? formatDate2(new Date(values.openDate).toISOString()) : undefined,
      closeDate: values.closeDate ? formatDate2(new Date(values.closeDate).toISOString()) : undefined,
    });
    setShowFilterModal(false);
  };

  const handleFilterClear = () => {
    setFilterParams(defaultParams);
    setShowFilterModal(false);
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'openDate',
      header: 'Open Date',
      accessorKey: 'openDate',
      cell: (props) => {
        const { getValue } = props;
        return <p>{formatDate3(getValue() as string)}</p>;
      },
    },
    {
      id: 'closeDate',
      header: 'Close Date',
      accessorKey: 'closeDate',
      cell: (props) => {
        const { getValue } = props;
        return <p>{formatDate3(getValue() as string)}</p>;
      },
    },
    {
      id: 'submittedDocuments',
      header: () => <p className='text-center text-xs font-bold text-neutralColor-normal capitalize'>Submitted Documents</p>,
      accessorKey: 'submittedDocuments',
      cell: (props) => {
        const { getValue } = props;
        return <p className='text-center'>{getValue() as number}</p>;
      },
    },
    {
      id: 'approvedDocuments',
      header: () => <p className='text-center text-xs font-bold text-neutralColor-normal capitalize'>Approved Documents</p>,
      accessorKey: 'approvedDocuments',
      cell: (props) => {
        const { getValue } = props;
        return <p className='text-center'>{getValue() as number}</p>;
      },
    },
    {
      id: 'submittedInvoices',
      header: () => <p className='text-center text-xs font-bold text-neutralColor-normal capitalize'>Submitted Invoices</p>,
      accessorKey: 'submittedInvoices',
      cell: (props) => {
        const { getValue } = props;
        return <p className='text-center'>{getValue() as number}</p>;
      },
    },
    {
      id: 'approvedInvoices',
      header: () => <p className='text-center text-xs font-bold text-neutralColor-normal capitalize'>Approved Invoices</p>,
      accessorKey: 'approvedInvoices',
      cell: (props) => {
        const { getValue } = props;
        return <p className='text-center'>{getValue() as number}</p>;
      },
    },
    {
      id: 'totalSubmittiedInvoiceAmount',
      header: () => <p className='text-center text-xs font-bold text-neutralColor-normal capitalize'>Total Invoices</p>,
      accessorKey: 'totalSubmittiedInvoiceAmount',
      cell: (props) => {
        const { getValue } = props;
        return (
          <p className='text-center'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </p>
        );
      },
    },
    {
      id: 'totalApprovedInvoiceAmount',
      header: () => <p className='text-center text-xs font-bold text-neutralColor-normal capitalize'>Approved Invoices</p>,
      accessorKey: 'totalApprovedInvoiceAmount',
      cell: (props) => {
        const { getValue } = props;
        return (
          <p className='text-center'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </p>
        );
      },
    },
  ];

  const nextPage = () => {
    const nextPage = filterParams.page + 1;
    if (nextPage <= pageCount) {
      setFilterParams((prev) => ({ ...prev, page: nextPage }));
    }
  };

  const previousPage = () => {
    const previousPage = filterParams.page - 1;
    if (previousPage > 0) {
      setFilterParams((prev) => ({ ...prev, page: previousPage }));
    }
  };

  const tablePagination = {
    pageCount,
    pageSize: filterParams.perPage,
    currentPage: filterParams.page,
    page: filterParams.page,
    setPageSize: (pageSize: number) =>
      setFilterParams((params) => ({
        ...params,
        perPage: pageSize,
      })),
    nextPage,
    previousPage,
  };

  return (
    <>
      <div className='flex flex-wrap gap-2 mb-4 mx-4'>
        <button
          className='p-2 rounded-md bg-gray-100 flex gap-1 items-center hover:bg-neutralColor-light cursor-pointer text-xs font-medium transition duration-300 ease-in-out'
          onClick={() => setShowFilterModal(true)}
        >
          <PlusIcon className='h-4 w-4' />
          <span>Add Filter</span>
        </button>
        {openDate && (
          <FilterBadge
            title={`Open Date: ${formatDate2(openDate)}`}
            onClear={() => {
              setFilterParams(({ openDate, ...rest }) => ({
                ...rest,
              }));
            }}
          />
        )}
        {closeDate && (
          <FilterBadge
            onClear={() => {
              setFilterParams(({ closeDate, ...rest }) => ({
                ...rest,
              }));
            }}
            title={`Close Date: ${formatDate2(closeDate)}`}
          />
        )}
        {invoiceApproverId?.value && (
          <FilterBadge
            onClear={() => {
              setFilterParams(({ invoiceApproverId, ...rest }) => ({
                ...rest,
              }));
            }}
            title={`Approver: ${invoiceApproverId?.label}`}
          />
        )}
      </div>
      {draws && draws.length === 0 && (
        <div className='text-center h-screen/2 flex flex-col justify-center items-center'>
          <InformationCircleIcon className='h-12 w-12 text-tertiaryColor-normal' />
          <h3 className='mt-1'>You have no any existing project draws</h3>
        </div>
      )}
      {draws && draws.length > 0 && (
        <Table
          cols={tableColumns}
          tableData={draws}
          pagination={tablePagination}
          onRowClick={(row) => navigate(`${location.pathname}/${row?.sequenceNum}`)}
        />
      )}
      {fetchDraws.isLoading && <LoadingTable />}

      {showFilterModal && (
        <Modal customStyle='overflow-visible'>
          <h2 className='m-0 text-primaryColor-normal'>Filter By</h2>
          <HookFormProvider defaultValues={filterParams} resolver={yupResolver(validationSchemaForFilter) as any}>
            <HookForm onSubmit={submitFilter} className='space-y-5'>
              <div className='grid grid-cols-2 gap-4'>
                <HookInput className='col-span-1' type='date' label='Open Date' name='openDate' />
                <HookInput className='col-span-1' type='date' label='Close Date' name='closeDate' />
                <HookReactSingleSelect
                  componentClassName='col-span-1'
                  label='Approver'
                  name='invoiceApproverId'
                  options={teams?.internal.map((team) => ({
                    label: team?.name,
                    value: team?.id,
                  }))}
                  placeholder=''
                  required
                />
              </div>

              <div className='flex gap-4 mt-5 justify-end'>
                <Button label='Clear' type='button' btnStyle='btn-neutral' onClick={handleFilterClear} />
                <Button label='Apply' type='submit' btnStyle='btn-primary' />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
    </>
  );
};
